import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const TIER_OPTIONS = [
  { key: 'foundations', label: 'Foundations', color: '#38bdf8' },
  { key: 'operator',    label: 'Operator',    color: '#34d399' },
  { key: 'certified',   label: 'Certified',   color: '#a78bfa' },
];

const MOCK_PROFILES = {
  none: { label: 'No profile (new student)', value: null },
  healthy: {
    label: 'Healthy — engaged, low risk',
    value: { engagementScore: 78, dropoutRisk: 12, fatigueScore: 20, riskSignals: [], totalSessions: 3 },
  },
  atrisk: {
    label: 'At-risk — low engagement, stalling',
    value: { engagementScore: 28, dropoutRisk: 72, fatigueScore: 60,
      riskSignals: ['lesson_stall', 'low_engagement_sustained', 'session_gap_widening'], totalSessions: 6 },
  },
  fatigued: {
    label: 'Fatigued — check-in needed',
    value: { engagementScore: 51, dropoutRisk: 40, fatigueScore: 82, fatigueCheckInNeeded: true,
      riskSignals: ['response_length_declining'], totalSessions: 5 },
  },
  returning: {
    label: 'Returning after 5 days away',
    value: { engagementScore: 60, dropoutRisk: 30, fatigueScore: 25, riskSignals: ['session_gap_widening'],
      totalSessions: 4, lastSessionAt: { _seconds: Math.floor((Date.now() - 5 * 86400000) / 1000) } },
  },
};

// Simple text renderer — splits on newlines, preserves line breaks
function MessageText({ text }) {
  return (
    <>
      {text.split('\n').map((line, i, arr) => (
        <React.Fragment key={i}>
          {line}
          {i < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

// Analyst card — shown in message stream when Two-Stage mode is active
function AnalystBubble({ directive }) {
  const stateColors = {
    BELOW_CONCEPT:  '#fbbf24',
    AT_CONCEPT:     '#4f8ef7',
    ABOVE_CONCEPT:  '#34d399',
    AVOIDANT:       '#f87171',
    FATIGUED:       '#a78bfa',
    BREAKTHROUGH:   '#00d4ff',
  };
  const color = stateColors[directive.student_state] || '#c4cde0';
  const urgencyColor = { normal: '#3a4560', elevated: '#fbbf24', intervention: '#f87171' }[directive.urgency] || '#3a4560';
  return (
    <div style={{
      padding: '12px 16px', borderRadius: '8px',
      background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.18)',
      fontSize: '12px', lineHeight: '1.6',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#a78bfa' }}>
          BACKGROUND ANALYST
        </span>
        <span style={{ fontSize: '10px', color: urgencyColor, fontWeight: 600, letterSpacing: '0.06em' }}>
          [{(directive.urgency || 'normal').toUpperCase()}]
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
        <span style={{ padding: '2px 8px', borderRadius: '4px', background: `${color}18`, border: `1px solid ${color}44`, color, fontSize: '11px', fontWeight: 600 }}>
          {directive.student_state}
        </span>
        <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(79,142,247,0.09)', border: '1px solid rgba(79,142,247,0.2)', color: '#4f8ef7', fontSize: '11px' }}>
          momentum: {directive.momentum}
        </span>
        <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(52,211,153,0.09)', border: '1px solid rgba(52,211,153,0.2)', color: '#34d399', fontSize: '11px', fontWeight: 600 }}>
          → {directive.recommended_move}
        </span>
      </div>
      {directive.pattern_note && (
        <div style={{ color: '#4a6080', fontSize: '12px', marginBottom: '8px', fontStyle: 'italic' }}>
          “{directive.pattern_note}”
        </div>
      )}
      <div style={{
        color: '#e2e8f0', fontSize: '13px', fontWeight: 600,
        borderLeft: '2px solid #a78bfa', paddingLeft: '10px',
      }}>
        {directive.teaching_directive}
      </div>
    </div>
  );
}

export default function AdminPromptLab() {
  const [mode, setMode]               = useState('training');  // 'training' | 'demo'
  const [tier, setTier]               = useState('foundations');
  const [profileKey, setProfileKey]   = useState('none');
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [error, setError]             = useState(null);

  // Training-mode state tracked client-side (no Firestore)
  const [lessonCount,   setLessonCount]   = useState(0);
  const [assessedLevel, setAssessedLevel] = useState(null);
  const [lessonBadges,  setLessonBadges]  = useState([]);  // log of completed lessons

  // Demo-mode state
  const [exchangeCount, setExchangeCount] = useState(0);
  const [demoComplete,  setDemoComplete]  = useState(false);
  const [primaryIssue,  setPrimaryIssue]  = useState(null);
  const [recommendedTier, setRecommendedTier] = useState(null);
  const [twoStage,      setTwoStage]      = useState(false);
  const [lastDirective, setLastDirective] = useState(null);

  const bottomRef    = useRef(null);
  const textareaRef  = useRef(null);
  const fns          = getFunctions();
  const promptTestChat = httpsCallable(fns, 'promptTestChat');

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-focus textarea whenever loading finishes
  useEffect(() => {
    if (!loading) textareaRef.current?.focus();
  }, [loading]);

  function resetSession() {
    setMessages([]);
    setInput('');
    setLoading(false);
    setSessionStarted(false);
    setError(null);
    setLessonCount(0);
    setAssessedLevel(null);
    setLessonBadges([]);
    setExchangeCount(0);
    setDemoComplete(false);
    setPrimaryIssue(null);
    setRecommendedTier(null);
    setLastDirective(null);
  }

  // Start session — triggers entry call (messages=[])
  async function startSession() {
    setLoading(true);
    setError(null);
    setSessionStarted(true);
    try {
      const result = await promptTestChat({
        messages: [],
        tier,
        mode,
        lessonCount: 0,
        assessedLevel: null,
        exchangeCount: 0,
        mockProfile: MOCK_PROFILES[profileKey].value,
      });
      const { reply } = result.data;
      setMessages([{ role: 'assistant', content: reply }]);
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (err) {
      setError(err.message || 'Failed to start session.');
      setSessionStarted(false);
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg  = { role: 'user', content: text };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const result = await promptTestChat({
        messages:      nextMsgs,
        tier,
        mode,
        lessonCount,
        assessedLevel,
        exchangeCount,
        twoStage: twoStage && mode === 'training',
        mockProfile: MOCK_PROFILES[profileKey].value,
      });

      const data = result.data;
      if (data.directive) setLastDirective(data.directive);
      setMessages(prev => {
        const additions = [];
        if (data.directive) additions.push({ role: 'analyst', content: data.directive });
        additions.push({ role: 'assistant', content: data.reply });
        return [...prev, ...additions];
      });

      if (mode === 'training') {
        if (data.lessonComplete) {
          const badge = `Lesson ${lessonCount + 1} complete${data.assessedLevel ? ` · ${data.assessedLevel}` : ''}`;
          setLessonBadges(prev => [...prev, badge]);
        }
        setLessonCount(data.lessonCount ?? lessonCount);
        setAssessedLevel(data.assessedLevel ?? assessedLevel);
      } else {
        setExchangeCount(data.exchangeCount ?? exchangeCount + 1);
        if (data.primaryIssue)    setPrimaryIssue(data.primaryIssue);
        if (data.recommendedTier) setRecommendedTier(data.recommendedTier);
        if (data.demoComplete)    setDemoComplete(true);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠ Error: ${err.message}` }]);
      setError(err.message);
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const tierColor = TIER_OPTIONS.find(t => t.key === tier)?.color || '#94a3b8';

  return (
    <div style={{ padding: '0 40px 40px' }}>

      {/* ── Header bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        gap: '12px', marginBottom: '20px',
      }}>
        <div style={{
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: '#a78bfa',
          background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)',
          borderRadius: '6px', padding: '4px 10px',
        }}>
          V2 Prompt Lab
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', border: '1px solid #1e2a3a', borderRadius: '6px', overflow: 'hidden' }}>
          {[{ key: 'training', label: 'Training' }, { key: 'demo', label: 'Demo' }].map(m => (
            <button key={m.key} onClick={() => { setMode(m.key); resetSession(); }}
              style={{
                padding: '5px 14px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer',
                background: mode === m.key ? '#1e3a5f' : 'transparent',
                color: mode === m.key ? '#4f8ef7' : '#6b7a99',
              }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Tier selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tier</span>
          <div style={{ display: 'flex', border: '1px solid #1e2a3a', borderRadius: '6px', overflow: 'hidden' }}>
            {TIER_OPTIONS.map(t => (
              <button key={t.key} onClick={() => { setTier(t.key); resetSession(); }}
                style={{
                  padding: '5px 12px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: tier === t.key ? '#0d1220' : 'transparent',
                  color: tier === t.key ? t.color : '#4a5568',
                  borderRight: '1px solid #1e2a3a',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mock profile selector */}
        {mode === 'training' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Profile</span>
            <select
              value={profileKey}
              onChange={e => { setProfileKey(e.target.value); resetSession(); }}
              style={{
                background: '#0a0e1a', border: '1px solid #1e2a3a', borderRadius: '6px',
                color: '#c4cde0', fontSize: '12px', padding: '4px 8px', cursor: 'pointer',
              }}
            >
              {Object.entries(MOCK_PROFILES).map(([k, v]) => (
                <option key={k} value={k} style={{ backgroundColor: '#0a0e1a' }}>{v.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Two-Stage toggle */}
        <button
          onClick={() => setTwoStage(p => !p)}
          style={{
            padding: '5px 14px',
            background: twoStage ? 'rgba(167,139,250,0.12)' : 'none',
            border: `1px solid ${twoStage ? '#a78bfa' : '#2a3550'}`,
            borderRadius: '6px',
            color: twoStage ? '#a78bfa' : '#6b7a99',
            fontSize: '12px', fontWeight: twoStage ? 700 : 400, cursor: 'pointer',
          }}
        >
          🔬 Two-Stage {twoStage ? 'ON' : 'OFF'}
        </button>

        {/* Reset button */}
        <button onClick={resetSession} style={{
          marginLeft: 'auto', padding: '5px 14px', background: 'none',
          border: '1px solid #2a3550', borderRadius: '6px',
          color: '#6b7a99', fontSize: '12px', cursor: 'pointer',
        }}>
          Reset
        </button>
      </div>

      {/* ── Status bar ── */}
      <div style={{
        display: 'flex', gap: '16px', alignItems: 'center',
        padding: '8px 14px', marginBottom: '16px',
        background: '#080d18', border: '1px solid #1a2235', borderRadius: '8px',
        fontSize: '11px', color: '#4a5568',
      }}>
        <span style={{ color: tierColor, fontWeight: 700 }}>{tier.toUpperCase()}</span>
        <span>•</span>
        <span style={{ color: mode === 'training' ? '#4f8ef7' : '#fbbf24' }}>
          {mode === 'training' ? 'Training mode' : 'Demo mode'}
        </span>
        {mode === 'training' && (
          <>
            <span>•</span>
            <span>Lesson <span style={{ color: '#f0f4ff', fontWeight: 600 }}>{lessonCount + 1}</span>/4</span>
            {assessedLevel && (
              <>
                <span>•</span>
                <span>Level <span style={{ color: '#a78bfa', fontWeight: 600 }}>{assessedLevel}</span></span>
              </>
            )}
          </>
        )}
        {mode === 'demo' && (
          <>
            <span>•</span>
            <span>Exchange <span style={{ color: '#f0f4ff', fontWeight: 600 }}>{exchangeCount}</span>/6</span>
            {primaryIssue && (
              <>
                <span>•</span>
                <span>Issue detected: <span style={{ color: '#fbbf24', fontWeight: 600 }}>{primaryIssue}</span></span>
              </>
            )}
            {recommendedTier && (
              <>
                <span>•</span>
                <span>→ <span style={{ color: '#34d399', fontWeight: 600 }}>{recommendedTier}</span></span>
              </>
            )}
          </>
        )}
        {profileKey !== 'none' && (
          <>
            <span>•</span>
            <span style={{ color: '#f87171', fontWeight: 600 }}>mock profile active</span>
          </>
        )}

        {/* Lesson badges */}
        {lessonBadges.map((b, i) => (
          <span key={i} style={{
            background: 'rgba(79,142,247,0.12)', border: '1px solid rgba(79,142,247,0.25)',
            borderRadius: '999px', padding: '2px 8px', color: '#4f8ef7', fontWeight: 600,
          }}>
            ✓ {b}
          </span>
        ))}
      </div>

      {/* ── Chat window ── */}
      <div style={{
        background: '#060b14', border: '1px solid #1a2235', borderRadius: '12px',
        minHeight: '400px', maxHeight: '560px', overflowY: 'auto',
        padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px',
        marginBottom: '12px',
      }}>
        {/* Pre-session state */}
        {!sessionStarted && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '13px', color: '#3a4560', marginBottom: '20px' }}>
              Configure your settings above, then start the session.
            </div>
            <button onClick={startSession} style={{
              padding: '10px 32px', background: '#1e3a5f',
              border: '1px solid #4f8ef7', borderRadius: '8px',
              color: '#4f8ef7', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              letterSpacing: '0.04em',
            }}>
              Start Session →
            </button>
          </div>
        )}

        {/* Loading entry response */}
        {loading && messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#3a4560', fontSize: '13px' }}>
            Starting session...
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => {
          if (msg.role === 'analyst') return <AnalystBubble key={i} directive={msg.content} />;
          return (
            <div key={i} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              {msg.role === 'assistant' && (
                <div style={{
                  fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: twoStage ? '#a78bfa' : '#4f8ef7', marginBottom: '4px', fontWeight: 700,
                }}>
                  INSTRUCTOR {twoStage && lastDirective ? '(V2 · DIRECTED)' : '(V2)'}
                </div>
              )}
              <div style={{
                maxWidth: '78%',
                padding: '12px 16px',
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                background: msg.role === 'user' ? '#1e3a5f' : '#0d1628',
                border: msg.role === 'user' ? '1px solid #2a4a70' : '1px solid #1a2a3a',
                color: msg.role === 'user' ? '#c4d8f0' : '#c4cde0',
                fontSize: '14px',
                lineHeight: '1.65',
                userSelect: msg.role === 'assistant' ? 'none' : 'text',
                WebkitUserSelect: msg.role === 'assistant' ? 'none' : 'text',
              }}>
                <MessageText text={msg.content} />
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {loading && messages.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4f8ef7', marginBottom: '4px', fontWeight: 700 }}>
              INSTRUCTOR (V2)
            </div>
            <div style={{
              padding: '12px 16px', borderRadius: '2px 12px 12px 12px',
              background: '#0d1628', border: '1px solid #1a2a3a',
              color: '#4a7090', fontSize: '14px',
            }}>
              thinking
              <span className="ptl-dots" style={{ marginLeft: '4px' }}>
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>
          </div>
        )}

        {/* Demo complete banner */}
        {demoComplete && (
          <div style={{
            padding: '16px', borderRadius: '8px',
            background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)',
            color: '#34d399', fontSize: '13px', textAlign: 'center',
          }}>
            Demo sequence complete.
            {recommendedTier && <span> Recommended tier: <strong>{recommendedTier}</strong>.</span>}
            {' '}
            <button onClick={resetSession} style={{
              background: 'none', border: 'none', color: '#34d399', cursor: 'pointer',
              textDecoration: 'underline', fontSize: '13px',
            }}>Run again</button>
          </div>
        )}

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: '6px',
            background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
            color: '#f87171', fontSize: '12px',
          }}>
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input bar ── */}
      {sessionStarted && !demoComplete && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading || !sessionStarted}
            placeholder='Type a student response and hit Enter…'
            rows={2}
            style={{
              flex: 1, background: '#0a0e1a', border: '1px solid #1e2a3a', borderRadius: '8px',
              color: '#f0f4ff', fontSize: '14px', padding: '10px 14px', resize: 'none',
              outline: 'none', fontFamily: "'Inter', sans-serif", lineHeight: '1.5',
              opacity: loading ? 0.5 : 1,
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{
              padding: '10px 22px', background: '#1e3a5f',
              border: '1px solid #4f8ef7', borderRadius: '8px',
              color: '#4f8ef7', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              opacity: (loading || !input.trim()) ? 0.4 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            Send
          </button>
        </div>
      )}

      {/* Hint */}
      <div style={{ marginTop: '10px', fontSize: '11px', color: '#2a3550', lineHeight: '1.6' }}>
        This lab calls <code style={{ color: '#4f8ef7' }}>promptTestChat</code> — admin-only, isolated from live sessions.
        Edit <code style={{ color: '#a78bfa' }}>functions/promptBuilders.v2.js</code> to swap in your V2 content, then redeploy.
        No Firestore writes. No rate limiting. No bans.
        <br />
        <span style={{ color: '#3a4060' }}>Two-Stage mode (training only): background analyst runs on</span>
        {' '}<code style={{ color: '#a78bfa', fontSize: '11px' }}>gpt-4o-mini</code>
        {' '}<span style={{ color: '#3a4060' }}>then passes a directive to the instructor on</span>
        {' '}<code style={{ color: '#4f8ef7', fontSize: '11px' }}>gpt-4o</code>
        <span style={{ color: '#3a4060' }}>. Both calls visible in stream. Each exchange uses 2 API calls.</span>
      </div>

      <style>{`
        @keyframes ptl-d1{0%,80%,100%{opacity:.2}20%{opacity:1}}
        @keyframes ptl-d2{0%,80%,100%{opacity:.2}40%{opacity:1}}
        @keyframes ptl-d3{0%,80%,100%{opacity:.2}60%{opacity:1}}
        .ptl-dots span:nth-child(1){animation:ptl-d1 1.2s infinite}
        .ptl-dots span:nth-child(2){animation:ptl-d2 1.2s infinite}
        .ptl-dots span:nth-child(3){animation:ptl-d3 1.2s infinite}
      `}</style>
    </div>
  );
}
